/* The one place that talks to the API.

   Three things it guarantees, so no caller has to remember them:

   - The session cookie travels (`credentials: "include"`), and the CSRF token the server
     stored on that session is copied from its readable cookie into the header. A
     cross-site page can make the browser send the cookie but cannot read it, which is what
     makes this a real check rather than a formality.
   - Every failure arrives in one shape. `422` carries per-field messages, `429` and the
     rest carry a sentence, and a network failure is not an exception the caller has to
     catch separately.
   - Requests time out. A fetch with no timeout hangs a screen forever on a dead network. */

const BASE = (
  import.meta.env.VITE_API_BASE || "http://127.0.0.1:8012"
).replace(/\/$/, "");

export const API = `${BASE}/api/v1`;
/* GitHub-facing redirects live at the root: their URLs are configured inside the App. */
export const ROOT = BASE;

const TIMEOUT_MS = 30_000;

function csrf() {
  const entry = document.cookie
    .split("; ")
    .find((item) => item.startsWith("codenerve_csrf="));
  return entry ? entry.slice("codenerve_csrf=".length) : "";
}

export class ApiError extends Error {
  constructor(message, { status = 0, fields = {} } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fields = fields;
  }
}

async function request(path, { method = "GET", body, signal } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  if (signal) signal.addEventListener("abort", () => controller.abort(), { once: true });

  let response;
  try {
    response = await fetch(API + path, {
      method,
      credentials: "include",
      signal: controller.signal,
      headers: {
        ...(body === undefined ? {} : { "content-type": "application/json" }),
        "x-csrf-token": csrf(),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError("Could not reach the server. Check your connection and try again.");
  } finally {
    clearTimeout(timer);
  }

  if (response.status === 204) return null;

  let data = {};
  try {
    data = await response.json();
  } catch {
    /* An empty or non-JSON body is not an error on its own. */
  }
  if (response.ok) return data;

  throw new ApiError(data.message || "Something went wrong. Try again.", {
    status: response.status,
    fields: data.fields || {},
  });
}

export const api = {
  get: (path, options) => request(path, options),
  post: (path, body, options) => request(path, { ...options, method: "POST", body: body ?? {} }),
  patch: (path, body, options) => request(path, { ...options, method: "PATCH", body: body ?? {} }),
  del: (path, options) => request(path, { ...options, method: "DELETE" }),
};

/* Query strings are built here so an undefined filter drops out rather than arriving as
   the literal string "undefined", which the server would then reject. */
export function query(params) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}
